import { Box, Flex, Text, LazyLoadListScrollTrigger } from '@fullstory/components';
import React, { useState } from 'react';
import { NotificationItem } from '../NotificationItem';
import { Notification } from '@fullstory/proto/fs/services/pinguin';
import { FormattedMessage } from 'react-intl';
import css from './index.module.scss';

export enum FilterType {
  unread = 'unread',
  account = 'Account',
  alerts = 'Alert',
  mentions = 'Mentions',
}

export interface FilteredNotificationsListProps {
  notifications: Notification[];
  filterType: FilterType;
  renderMore: () => void;
  // The height of the filters to be used to calculate the height of this component for scrolling
  heightOffset: number;
  onClickCta: (notification: Notification) => void;
}

/*
  This offset accounts for the top and bottom padding (16 px each) on the parent box as well as
  the sidebar border, which is 2px wide. Combined this gives an additional offset of 36px.
*/
const ADDITIONAL_HEIGHT_OFFSET = 36;

interface EmptyStateInterface {
  filterType: FilterType;
}

const EmptyState: React.FC<EmptyStateInterface> = ({ filterType }) => (
  <Flex data-component="EmptyState" alignItems="center" className={css.emptyState}>
    <Text appearance="bodyMedium" color="secondary">
      <FormattedMessage
        defaultMessage="There are no {filterType} notifications."
        id="BxDa2M"
        values={{ filterType: filterType.toLowerCase() }}
      />
    </Text>
  </Flex>
);

export const FilteredNotificationsList: React.FC<FilteredNotificationsListProps> = ({
  notifications,
  filterType,
  renderMore,
  heightOffset,
  onClickCta,
}) => {
  const [notificationListRef, setNotificationListRef] = useState<HTMLDivElement | null>(null);

  return (
    <Box
      data-component="FilteredNotificationsList"
      ref={setNotificationListRef}
      overflow="scroll"
      height={`calc(100% - ${heightOffset}px - ${ADDITIONAL_HEIGHT_OFFSET}px)`}
    >
      <Box marginTop={12}>
        {notifications.length ? (
          notifications.map(notification => (
            <Box marginBottom={8} key={notification.id}>
              <NotificationItem
                notification={notification}
                showTimestamp
                onClickCta={() => onClickCta(notification)}
              />
            </Box>
          ))
        ) : (
          <EmptyState filterType={filterType} />
        )}
      </Box>
      <div className={css.gradient} />
      <LazyLoadListScrollTrigger
        rootRef={notificationListRef}
        renderMore={renderMore}
        key={notifications.length}
      />
    </Box>
  );
};
